import Editor, {
  BeforeMount,
  Monaco,
  OnChange,
  OnMount,
} from '@monaco-editor/react';
import history from 'history/browser';
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import themes, { Theme } from '../style/themes';

import type { editor } from 'monaco-editor';
import { ResetFunction } from './Editor';
import { detectLanguage } from '../util/language-detection';
import { logLanguage } from '../util/log-language';

export interface EditorTextAreaProps {
  forcedContent: string;
  actualContent: string;
  setActualContent: (value: string) => void;
  theme: Theme;
  language: string;
  fontSize: number;
  readOnly: boolean;
  resetFunction: MutableRefObject<ResetFunction | undefined>;
  onLanguageDetected?: (detectedLanguage: string) => void;
}

export default function EditorTextArea({
  forcedContent,
  actualContent,
  setActualContent,
  theme,
  language,
  fontSize,
  readOnly,
  resetFunction,
  onLanguageDetected,
}: EditorTextAreaProps) {
  const [editor, setEditor] = useState<editor.IStandaloneCodeEditor>();
  const [monaco, setMonaco] = useState<Monaco>();
  const [selected, toggleSelected] = useSelectedLine();
  const editorAreaRef = useRef<HTMLDivElement>(null);

  // Paste detection state
  const lastContentLengthRef = useRef<number>(0);
  const pasteDisposableRef = useRef<{ dispose: () => void } | null>(null);
  const initialEmptyRef = useRef<boolean>(true); // whether editor was empty at mount/last-clear
  const pasteDetectionEnabledRef = useRef<boolean>(true); // allow a single detection by default

  useLineNumberMagic(
    editorAreaRef,
    selected,
    toggleSelected,
    forcedContent,
    editor,
    monaco
  );

  usePlaceholderText(actualContent, editor, monaco);

  // If forcedContent is cleared externally, reset the empty/paste detection flags
  useEffect(() => {
    if (forcedContent === '') {
      console.debug('[EditorTextArea] forcedContent cleared -> resetting empty/paste detection flags');
      initialEmptyRef.current = true;
      pasteDetectionEnabledRef.current = true;
      lastContentLengthRef.current = 0;
    }
  }, [forcedContent]);

  const beforeMount: BeforeMount = monaco => {
    for (const theme of Object.values(themes) as Theme[]) {
      monaco.editor.defineTheme(theme.id, theme.editor);
    }

    monaco.languages.register({ id: 'log' });
    monaco.languages.setMonarchTokensProvider('log', logLanguage);

    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: true,
    });
  };

  const onMount: OnMount = (editor, monaco) => {
    editor.addAction({
      id: 'search',
      label: 'Search with Google',
      contextMenuGroupId: '9_cutcopypaste',
      contextMenuOrder: 5,
      run: editor => {
        const selection = editor.getSelection();
        if (selection && !selection.isEmpty()) {
          const model = editor.getModel();
          if (model) {
            const query = model.getValueInRange(selection);
            window.open('https://www.google.com/search?q=' + encodeURIComponent(query), '_blank');
          }
        }
      },
    });

    setEditor(editor);
    setMonaco(monaco);

    resetFunction.current = () => {
      console.debug('[EditorTextArea] resetFunction: clearing editor and resetting paste-detection state');
      editor.setValue('');
      editor.focus();
      lastContentLengthRef.current = 0;
      initialEmptyRef.current = true;
      pasteDetectionEnabledRef.current = true;
    };

    // Initialize paste detection state based on current model value
    const model = editor.getModel();
    const initialLength = model?.getValue().length || 0;
    lastContentLengthRef.current = initialLength;
    initialEmptyRef.current = initialLength === 0;
    pasteDetectionEnabledRef.current = initialEmptyRef.current;

    console.debug('[EditorTextArea] onMount: initialLength=%d, initialEmpty=%s, pasteDetectionEnabled=%s',
      initialLength, initialEmptyRef.current, pasteDetectionEnabledRef.current);

    // Setup model change listener for paste detection
    if (model) {
      pasteDisposableRef.current = model.onDidChangeContent((e: editor.IModelContentChangedEvent) => {
        try {
          // Update content early
          const content = model.getValue();
          const currentLength = content.length;
          const prevLength = lastContentLengthRef.current;
          const delta = currentLength - prevLength;

          console.debug('[EditorTextArea] onDidChangeContent fired', {
            changesCount: Array.isArray(e.changes) ? e.changes.length : 0,
            isFlush: (e as any).isFlush ?? false,
            isUndoing: (e as any).isUndoing ?? false,
            isRedoing: (e as any).isRedoing ?? false,
            prevLength,
            currentLength,
            delta,
          });

          // If the editor was cleared (user deleted or code cleared), reset detection flags
          if (currentLength === 0) {
            if (!initialEmptyRef.current || !pasteDetectionEnabledRef.current) {
              console.debug('[EditorTextArea] editor cleared -> re-enabling paste detection for next paste');
            }
            initialEmptyRef.current = true;
            pasteDetectionEnabledRef.current = true;
            lastContentLengthRef.current = 0;
            return;
          }

          // Minimal guard: don't run detection if callback not provided
          if (!onLanguageDetected) {
            lastContentLengthRef.current = currentLength;
            return;
          }

          // Detect paste via change objects
          let largeInsertDetected = false;
          if (Array.isArray(e.changes)) {
            for (const ch of e.changes) {
              const textLen = typeof ch.text === 'string' ? ch.text.length : 0;
              console.debug('[EditorTextArea] change item', { textLen, rangeLength: ch.rangeLength, range: (ch as any).rangeOffset ?? null });
              // Heuristic: single change inserting a lot of text or replacing a large range
              if (textLen >= 80) {
                largeInsertDetected = true;
                break;
              }
              if ((ch as any).rangeLength >= 80 && textLen > 0) {
                largeInsertDetected = true;
                break;
              }
            }
          }

          // Fallback heuristic: small -> big in one step
          const fallbackHeuristic = prevLength <= 10 && currentLength >= 80;

          console.debug('[EditorTextArea] detection flags', { largeInsertDetected, fallbackHeuristic, pasteDetectionEnabled: pasteDetectionEnabledRef.current, initialEmpty: initialEmptyRef.current });

          if (pasteDetectionEnabledRef.current && (largeInsertDetected || fallbackHeuristic)) {
            console.info('[EditorTextArea] Large paste detected; running language detection');
            const detected = detectLanguage(content);
            console.info('[EditorTextArea] detectLanguage ->', detected);

            // Only call back if detection seems meaningful
            if (detected && detected !== 'plain') {
              console.info('[EditorTextArea] invoking onLanguageDetected with', detected);
              try {
                onLanguageDetected(detected);
              } catch (cbErr) {
                console.error('[EditorTextArea] error calling onLanguageDetected callback', cbErr);
              }
            } else {
              console.info('[EditorTextArea] detected language deemed "plain" or not confident; still logging result:', detected);
            }

            // disable further detections until cleared
            pasteDetectionEnabledRef.current = false;
            initialEmptyRef.current = false;
            console.debug('[EditorTextArea] pasteDetectionEnabled set to false');
          }

          // Always update recorded length (unless we returned earlier for empty)
          lastContentLengthRef.current = currentLength;
        } catch (err) {
          console.error('[EditorTextArea] error in paste detection handler', err);
        }
      });
    }

    // focus editor after mount
    editor.focus();
  };

  const onChange: OnChange = useCallback(
    value => {
      setActualContent(value as string);
    },
    [setActualContent]
  );

  // detect indentation whenever new forced content is set
  useEffect(() => {
    if (!editor) {
      return;
    }

    const model = editor.getModel();
    if (!model) {
      return;
    }

    model.detectIndentation(true, 2);
  }, [editor, forcedContent]);

  // cleanup paste event listener on unmount
  useEffect(() => {
    return () => {
      if (pasteDisposableRef.current) {
        try {
          pasteDisposableRef.current.dispose();
          console.debug('[EditorTextArea] disposed paste listener on unmount');
        } catch (err) {
          console.error('[EditorTextArea] error disposing paste listener', err);
        }
      }
    };
  }, []);

  return (
    <EditorArea ref={editorAreaRef}>
      <Editor
        theme={theme.id}
        language={language === 'plain' ? 'plaintext' : language}
        options={{
          fontFamily: 'JetBrains Mono',
          fontSize: fontSize,
          fontLigatures: true,
          wordWrap: 'on',
          renderLineHighlight: 'none',
          renderValidationDecorations: 'off',
          readOnly,
          domReadOnly: readOnly,
        }}
        beforeMount={beforeMount}
        onMount={onMount}
        onChange={onChange}
        value={forcedContent}
      />
    </EditorArea>
  );
}

const EditorArea = styled.main`
  margin-top: 2.5em;
  height: 100%;

  .line-numbers {
    cursor: pointer !important;
  }

  .highlighted-line + div {
    color: ${props => props.theme.highlightedLine.color};
    background-color: ${props => props.theme.highlightedLine.backgroundColor};
    font-weight: bold;
  }

  .placeholder-text {
    position: absolute;
    opacity: 0.5;
    font-weight: lighter;

    &::after {
      content: 'Paste (or type) some code...';
    }
  }
`;

function usePlaceholderText(
  actualContent: string,
  editor: editor.IStandaloneCodeEditor | undefined,
  monaco: Monaco | undefined
) {
  useEffect(() => {
    if (!editor || !monaco || actualContent) {
      return;
    }
    const decoration = {
      range: new monaco.Range(1, 0, 1, 1),
      options: {
        after: {
          content: '\u200B',
          inlineClassName: 'placeholder-text',
        },
      },
    };
    const decorations = editor.deltaDecorations([], [decoration]);

    return () => {
      editor.deltaDecorations(decorations, []);
    };
  }, [editor, monaco, actualContent]);
}

type SelectedLine = [number, number];
type ToggleSelectedFunction = (lineNo: number, e: MouseEvent) => void;

function useSelectedLine(): [SelectedLine, ToggleSelectedFunction] {
  // extract highlighted lines from window hash
  const [selected, setSelected] = useState<[number, number]>(() => {
    const hash = window.location.hash;
    if (/^#L\d+(-\d+)?$/.test(hash)) {
      const [start, end] = hash.substring(2).split('-').map(Number);
      return [start, isNaN(end) ? start : end];
    } else {
      return [-1, -1];
    }
  });

  // update window hash when a new line is highlighted
  useEffect(() => {
    let hash = '';

    if (selected[0] !== -1) {
      if (selected[1] !== selected[0]) {
        const start = Math.min(...selected);
        const end = Math.max(...selected);
        hash = `#L${start}-${end}`;
      } else {
        hash = `#L${selected[0]}`;
      }
    }

    history.replace({ hash });
  }, [selected]);

  // toggle the highlighting for a given line
  const toggleSelected = useCallback(
    (lineNo: number, e: MouseEvent) => {
      const shift = e.shiftKey;
      if (selected[0] === lineNo && selected[1] === lineNo) {
        setSelected([-1, -1]);
      } else if (selected[0] === -1 || !shift) {
        setSelected([lineNo, lineNo]);
      } else {
        setSelected([selected[0], lineNo]);
      }
    },
    [selected, setSelected]
  );

  return [selected, toggleSelected];
}

function useLineNumberMagic(
  editorAreaRef: React.RefObject<HTMLDivElement>,
  selected: SelectedLine,
  toggleSelected: ToggleSelectedFunction,
  forcedContent: string,
  editor: editor.IStandaloneCodeEditor | undefined,
  monaco: Monaco | undefined
) {
  // add an event listener for clicking on line numbers
  useEffect(() => {
    const node = editorAreaRef.current;
    if (!node) {
      return;
    }

    const handler = (click: MouseEvent) => {
      const target = click?.target as HTMLElement;
      if (
        target &&
        target.classList.contains('line-numbers') &&
        target.textContent
      ) {
        toggleSelected(parseInt(target.textContent), click);
      }
    };

    node.addEventListener('click', handler);
    return () => node.removeEventListener('click', handler);
  }, [editorAreaRef, toggleSelected]);

  // apply a 'highlighed' decoration to the selected lines
  // and scroll them into view if not already
  useEffect(() => {
    if (!editor || !monaco || selected[0] === -1) {
      return;
    }

    // apply a decoration
    const newDecorations = [
      {
        range: new monaco.Range(selected[0], 1, selected[1], 1),
        options: {
          isWholeLine: true,
          linesDecorationsClassName: 'highlighted-line',
        },
      },
    ];
    const decorations = editor.deltaDecorations([], newDecorations);

    // scroll the selected line into view
    if (selected[0] === selected[1]) {
      editor.revealLineInCenterIfOutsideViewport(selected[0]);
    } else {
      editor.revealLinesInCenterIfOutsideViewport(selected[0], selected[1]);
    }

    // cleanup by removing the decorations
    return () => {
      editor.deltaDecorations(decorations, []);
    };
  }, [editor, monaco, selected, forcedContent]);
}
